import { useEffect, useState } from 'react';
import {
  ChakraProvider,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  Button,
  VStack,
  Text,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
} from '@chakra-ui/react';
import type { ImageProps } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Upload, FileText, LogOut, BarChart3, CreditCard, Activity, Shield, KeyRound, ChevronDown } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import DraftsList from './components/DraftsList';
import SubmitContent from './components/SubmitContent';
import Login from './components/Login';
import Signup from './components/Signup';
import SetPassword from './components/SetPassword';
import BillingBanner from './components/BillingBanner';
import UsageDashboard from './components/UsageDashboard';
import AdminDashboard from './components/AdminDashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import ChangePasswordModal from './components/ChangePasswordModal';
import { createPortalSession } from './lib/api';
import theme from './theme';

// Site navigation is data-driven so adding a site is a one-line change.
// `key` is the UI selector; `backendId` is the site_id sent to the API.
type SiteId = 'mgc' | 'creditily' | 'mesafinanciera';

interface SiteNavItem {
  key: SiteId;
  backendId: string;
  label: string;
  logo: string;
  imgProps: ImageProps;
  // When true, the logo is a wordmark (already contains the brand name), so the
  // text label is hidden and the logo alone identifies the tab.
  hideLabel?: boolean;
}

const SITES: SiteNavItem[] = [
  {
    key: 'mgc',
    backendId: 'mgc',
    label: 'MGC Blog',
    logo: '/images/default-profile-image.png',
    imgProps: { boxSize: '20px', objectFit: 'cover', borderRadius: '4px' },
  },
  {
    key: 'creditily',
    backendId: 'getcreditily',
    label: 'Creditily Blog',
    logo: '/images/creditily-favicon-style-logo.png',
    imgProps: { h: '20px', objectFit: 'contain' },
  },
  {
    key: 'mesafinanciera',
    backendId: 'mesafinanciera',
    label: 'Mesa Financiera',
    logo: '/images/mesa-financiera-logo.png',
    imgProps: { h: '24px', objectFit: 'contain' },
    hideLabel: true,
  },
];

// Tab indices — keep in sync with the <Tab> order in <SiteDashboard>
const TAB_GENERATE = 0;
const TAB_DRAFTS = 1;
const TAB_USAGE = 2;

function Dashboard() {
  const { user, userRole, isPlatformAdmin, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSite, setActiveSite] = useState<SiteId>('mgc');
  const [tabIndex, setTabIndex] = useState(
    location.pathname === '/billing' ? TAB_USAGE : TAB_GENERATE,
  );
  const [openingPortal, setOpeningPortal] = useState(false);
  const { isOpen: isPwOpen, onOpen: onPwOpen, onClose: onPwClose } = useDisclosure();

  const isAdminOrOwner = userRole === 'owner' || userRole === 'admin';

  // Keep tab and URL in sync. /billing always forces the Usage tab. When the
  // URL leaves /billing (e.g. browser back), reset to Generate only if we were
  // still on Usage — otherwise preserve whichever tab handleTabChange just set.
  useEffect(() => {
    if (location.pathname === '/billing') {
      setTabIndex((cur) => (cur === TAB_USAGE ? cur : TAB_USAGE));
    } else {
      setTabIndex((cur) => (cur === TAB_USAGE ? TAB_GENERATE : cur));
    }
  }, [location.pathname]);

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    if (index === TAB_USAGE && location.pathname !== '/billing') {
      navigate('/billing');
    } else if (index !== TAB_USAGE && location.pathname === '/billing') {
      navigate('/');
    }
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, white, brand.50, white)"
      py={8}
      px={4}
    >
      <Box maxW="1400px" mx="auto" w="full">
        <VStack spacing={6} align="stretch">
          {/* Billing Banner */}
          <BillingBanner />

          {/* Header */}
          <Flex
            bg="white"
            px={6}
            py={3}
            borderRadius="xl"
            boxShadow="sm"
            borderWidth={1}
            borderColor="gray.200"
            align="center"
            gap={4}
          >
            {/* Left: Brand Logo */}
            <HStack spacing={3} flex="1" minW={0}>
              <Image
                src="/images/mesa-logo-for-light-bg.png"
                alt="Mesa Group Consulting"
                h="34px"
                objectFit="contain"
                flexShrink={0}
              />
            </HStack>

            {/* Center: Site Navigation */}
            <HStack
              spacing={1}
              bg="gray.100"
              p={1}
              borderRadius="lg"
              flexShrink={0}
            >
              {SITES.map((site) => {
                const active = activeSite === site.key;
                return (
                  <Button
                    key={site.key}
                    size="sm"
                    px={4}
                    fontWeight="600"
                    fontSize="sm"
                    borderRadius="md"
                    bg={active ? 'white' : 'transparent'}
                    color={active ? 'gray.900' : 'gray.500'}
                    boxShadow={active ? 'sm' : 'none'}
                    onClick={() => setActiveSite(site.key)}
                    _hover={{
                      color: 'gray.900',
                      bg: active ? 'white' : 'gray.50',
                    }}
                    transition="all 0.2s"
                  >
                    <HStack spacing={2}>
                      <Image
                        src={site.logo}
                        alt={site.hideLabel ? site.label : ''}
                        {...site.imgProps}
                      />
                      {!site.hideLabel && <span>{site.label}</span>}
                    </HStack>
                  </Button>
                );
              })}
            </HStack>

            {/* Right: User account menu */}
            <HStack spacing={3} flex="1" justify="flex-end" minW={0}>
              <Menu placement="bottom-end" autoSelect={false}>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="ghost"
                  rightIcon={<ChevronDown size={16} />}
                  color="gray.600"
                  fontWeight="500"
                  maxW={{ base: '160px', md: '260px' }}
                  _hover={{ color: 'gray.900', bg: 'gray.100' }}
                  _active={{ bg: 'gray.100' }}
                >
                  <Text as="span" isTruncated display="block">
                    {user?.email}
                  </Text>
                </MenuButton>
                <MenuList minW="220px">
                  {isAdminOrOwner && (
                    <MenuItem
                      icon={<CreditCard size={16} />}
                      isDisabled={openingPortal}
                      onClick={async () => {
                        try {
                          setOpeningPortal(true);
                          const { url } = await createPortalSession();
                          window.location.href = url;
                        } catch {
                          setOpeningPortal(false);
                        }
                      }}
                    >
                      Billing
                    </MenuItem>
                  )}
                  <MenuItem icon={<KeyRound size={16} />} onClick={onPwOpen}>
                    Change Password
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem icon={<LogOut size={16} />} onClick={signOut} color="red.500">
                    Sign Out
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
          <ChangePasswordModal isOpen={isPwOpen} onClose={onPwClose} />

          {/* Main Content */}
          <SiteDashboard
            siteId={(SITES.find((s) => s.key === activeSite) ?? SITES[0]).backendId}
            tabIndex={tabIndex}
            onTabChange={handleTabChange}
            isAdminOrOwner={isAdminOrOwner}
            isPlatformAdmin={isPlatformAdmin}
          />
        </VStack>
      </Box>
    </Box>
  );
}

interface SiteDashboardProps {
  siteId: string;
  tabIndex: number;
  onTabChange: (index: number) => void;
  isAdminOrOwner: boolean;
  isPlatformAdmin: boolean;
}

function SiteDashboard({ siteId, tabIndex, onTabChange, isAdminOrOwner, isPlatformAdmin }: SiteDashboardProps) {
  const tabStyle = {
    fontWeight: '500' as const,
    color: 'gray.600',
    _selected: {
      color: 'brand.700',
      borderColor: 'brand.500',
      bg: 'white',
      borderTopRadius: 'lg',
      borderBottomColor: 'transparent',
    },
    _hover: { color: 'gray.900' },
    transition: 'all 0.2s',
  };

  return (
    <Box
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth={1}
      borderColor="gray.200"
      overflow="hidden"
      minH="calc(100vh - 180px)"
    >
      <Tabs colorScheme="brand" variant="line" index={tabIndex} onChange={onTabChange}>
        <TabList borderBottomWidth={2} borderColor="gray.200" bg="gray.50">
          <Tab {...tabStyle}>
            <HStack spacing={2}><Upload size={18} /><span>Generate</span></HStack>
          </Tab>
          <Tab {...tabStyle}>
            <HStack spacing={2}><FileText size={18} /><span>Drafts</span></HStack>
          </Tab>
          <Tab {...tabStyle}>
            <HStack spacing={2}><Activity size={18} /><span>Usage</span></HStack>
          </Tab>
          {isAdminOrOwner && (
            <Tab {...tabStyle}>
              <HStack spacing={2}><BarChart3 size={18} /><span>Admin</span></HStack>
            </Tab>
          )}
          {isPlatformAdmin && (
            <Tab {...tabStyle}>
              <HStack spacing={2}><Shield size={18} /><span>Platform</span></HStack>
            </Tab>
          )}
        </TabList>

        <TabPanels bg="gray.50">
          <TabPanel p={8}>
            <SubmitContent siteId={siteId} />
          </TabPanel>
          <TabPanel p={8}>
            <DraftsList siteId={siteId} />
          </TabPanel>
          <TabPanel p={0}>
            <UsageDashboard />
          </TabPanel>
          {isAdminOrOwner && (
            <TabPanel p={0}>
              <AdminDashboard />
            </TabPanel>
          )}
          {isPlatformAdmin && (
            <TabPanel p={0}>
              <SuperAdminDashboard />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );
}

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
